import {
  FiAlertCircle,
  FiArchive,
  FiCalendar,
  FiClipboard,
  FiCreditCard,
  FiFileText,
  FiGlobe,
  FiGrid,
  FiHome,
  FiImage,
  FiLink,
  FiMail,
  FiMapPin,
  FiPackage,
  FiSettings,
  FiShoppingBag,
  FiShield,
  FiSliders,
  FiTruck,
  FiUsers,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';

/**
 * Icon registry.
 *
 * Navigation items declare icons by name (a string), not by direct component
 * import. This keeps `MODULE_NAVIGATION` free of UI-framework coupling and lets
 * the layout layer resolve names to icons in one place.
 *
 * Icons come from Feather (`react-icons/fi`) and ONLY from there — see the icon
 * rule in AGENTS.md. A second icon library is how a UI grows two slightly
 * different arrow glyphs.
 */
export const iconRegistry: Record<string, IconType> = {
  dashboard: FiGrid,
  users: FiUsers,
  package: FiPackage,
  shoppingBag: FiShoppingBag,
  store: FiShoppingBag,
  truck: FiTruck,
  warehouse: FiArchive,
  fileText: FiFileText,
  clipboardList: FiClipboard,
  calendar: FiCalendar,
  building2: FiPackage,  // Using FiPackage as building2 alternative
  mapPin: FiMapPin,
  shield: FiShield,
  palette: FiSettings,  // Using FiSettings as palette alternative
  alertCircle: FiAlertCircle,
  settings: FiSettings,
  sliders: FiSliders,
  megaphone: FiGlobe,  // Using FiGlobe as megaphone alternative
  globe: FiGlobe,
  home: FiHome,
  creditCard: FiCreditCard,
  image: FiImage,
  link: FiLink,
  mail: FiMail,

  /**
   * The placeholder for a destination that has no icon of its own.
   *
   * A gallery glyph rather than a blank space or a generic dot: a sidebar where
   * some rows carry a mark and others do not reads as broken, and the eye uses
   * the icon column to scan. A placeholder keeps the column aligned and says
   * "this is a destination" without pretending to describe it.
   *
   * It is deliberately NOT a question mark or a warning triangle — those read as
   * an error, and a missing icon is not a fault the user should be told about.
   */
  gallery: FiImage,
};

/**
 * Resolve an icon name to its component. Returns `null` for an unknown name so
 * a navigation item with a missing icon degrades gracefully instead of crashing
 * the shell.
 */
export function resolveIcon(name: string | undefined): IconType | null {
  if (!name) return null;
  return iconRegistry[name] ?? null;
}

/**
 * Renders a registered icon by name, or nothing when the name is unknown.
 *
 * A lowercase render helper rather than a `RegistryIcon` component, and that is
 * not a style choice: `react-hooks/static-components` flags ANY capitalized
 * function that calls another function and renders the result, because it
 * cannot tell a registry lookup from a component defined inline. Wrapping the
 * lookup in a component merely moves the warning. A helper is rendered by its
 * caller, so no component value is ever manufactured during render.
 *
 * Prefer this over `const Icon = resolveIcon(...)` at a call site for the same
 * reason — that form is exactly what the rule reports.
 */
export function renderIcon(name?: string, className?: string) {
  const Icon = resolveIcon(name);
  if (!Icon) return null;
  return <Icon className={className} aria-hidden="true" />;
}

export type { IconType };
